import { useState, useEffect, useCallback, useRef } from 'react';

const QUEUE_KEY = 'konnect_offline_queue';

/**
 * Hook for field service offline mode.
 * - Detects online/offline state
 * - Queues failed API requests for replay when back online
 * - Registers the field service worker
 * - Shows sync status
 */
export function useOfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueCount, setQueueCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const swRegistered = useRef(false);

  // Register field service worker
  useEffect(() => {
    if (swRegistered.current) return;
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw-field.js')
        .then(() => { swRegistered.current = true; })
        .catch(() => {});

      // Listen for queued items from SW
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data.type === 'OFFLINE_QUEUED') {
          addToQueue(event.data.item);
        }
        if (event.data.type === 'SYNC_REQUESTED') {
          syncQueue();
        }
      });
    }
  }, []);

  // Online/offline detection
  useEffect(() => {
    const goOnline = () => { setIsOnline(true); syncQueue(); };
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  // Load queue count on mount
  useEffect(() => {
    const queue = getQueue();
    setQueueCount(queue.length);
  }, []);

  const getQueue = () => {
    try {
      return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
    } catch {
      return [];
    }
  };

  const addToQueue = (item) => {
    const queue = getQueue();
    queue.push(item);
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
    setQueueCount(queue.length);
  };

  const clearQueue = () => {
    localStorage.setItem(QUEUE_KEY, '[]');
    setQueueCount(0);
  };

  const syncQueue = useCallback(async () => {
    const queue = getQueue();
    if (queue.length === 0 || !navigator.onLine) return;

    setSyncing(true);
    const failed = [];

    for (const item of queue) {
      try {
        await fetch(item.url, {
          method: item.method,
          headers: item.headers,
          body: item.body,
        });
      } catch {
        failed.push(item);
      }
    }

    localStorage.setItem(QUEUE_KEY, JSON.stringify(failed));
    setQueueCount(failed.length);
    setSyncing(false);
  }, []);

  /**
   * Wrapper for fetch that queues on failure (offline mode)
   */
  const offlineFetch = useCallback(async (url, options = {}) => {
    try {
      const response = await fetch(url, options);
      return response;
    } catch (error) {
      if (!navigator.onLine && ['POST', 'PUT'].includes(options.method || 'GET')) {
        addToQueue({
          url,
          method: options.method || 'GET',
          headers: options.headers || {},
          body: options.body || null,
          timestamp: Date.now(),
        });
        return new Response(JSON.stringify({ ok: true, queued: true, offline: true }), {
          status: 200,
          headers: { 'Content-Type': 'application/json' },
        });
      }
      throw error;
    }
  }, []);

  return {
    isOnline,
    queueCount,
    syncing,
    syncQueue,
    clearQueue,
    offlineFetch,
  };
}
